/**
 * AuiStatCard — Single KPI metric card.
 *
 * Pure Tailwind component (no ECharts). Shows a title, large value,
 * optional trend indicator with percentage, and optional description.
 *
 * Props:
 *   title       — metric label ("Total Revenue")
 *   value       — display value (string or number)
 *   trend       — percentage change (positive = up, negative = down)
 *   trendLabel  — context text after the trend ("from last month")
 *   description — secondary text below the value
 */

import { cn } from '@/lib/cn';
import { SparklineChart } from '@/components/ui/sparkline-chart';
import type { AuiRendererProps } from '../../registry';

export function AuiStatCard({ node }: AuiRendererProps) {
  const props = node.component.props ?? {};
  const title = (props.title as string) ?? '';
  const value = props.value as string | number | undefined;
  const trend = props.trend as number | undefined;
  const trendLabel = (props.trendLabel as string) ?? '';
  const description = props.description as string | undefined;
  const index = props.index as number | undefined;
  const sparklineData = props.sparklineData as number[] | undefined;
  const sparklineVariant = (props.sparklineVariant as 'line' | 'bar') ?? 'line';
  const sparklineColor = props.sparklineColor as 1 | 2 | 3 | 4 | 5 | string | undefined;

  const hasTrend = trend !== undefined;
  const trendUp = hasTrend && trend > 0;
  const trendDown = hasTrend && trend < 0;

  const showDividerDesktop = typeof index === 'number' && index > 0;
  const showDividerTablet = typeof index === 'number' && index % 2 === 1;

  return (
    <article
      className="relative flex min-h-36 flex-col justify-center p-4 sm:p-5"
      data-testid="aui-stat-card"
    >
      {/* Vertical divider between cards (visible on tablet+ when inside StatsOverview) */}
      <div
        aria-hidden="true"
        className={cn(
          'absolute left-0 top-4 bottom-4 hidden w-px bg-border',
          showDividerTablet && 'sm:max-lg:block',
          showDividerDesktop && 'lg:block',
        )}
      />

      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-muted-foreground">{title}</p>
      </div>

      <div className="mt-2 flex items-end justify-between gap-3">
        <p className="text-3xl font-semibold tracking-tight tabular-nums">
          {value ?? '—'}
        </p>
        {sparklineData && sparklineData.length > 0 && (
          <div className="w-24 shrink-0">
            <SparklineChart
              data={sparklineData}
              variant={sparklineVariant}
              height={36}
              color={sparklineColor}
              showArea
            />
          </div>
        )}
      </div>

      {description && (
        <p className="mt-1 text-xs text-muted-foreground">{description}</p>
      )}

      {hasTrend && (
        <div className="mt-1.5 flex items-center gap-1.5 text-sm">
          <span
            className={cn(
              'flex items-center gap-0.5 font-medium',
              trendUp && 'text-status-success-fg',
              trendDown && 'text-status-error-fg',
              !trendUp && !trendDown && 'text-muted-foreground',
            )}
          >
            {/* Inline SVG arrows to avoid lucide dependency */}
            {trendUp && (
              <svg className="size-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
                <polyline points="16 7 22 7 22 13" />
              </svg>
            )}
            {trendDown && (
              <svg className="size-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="22 17 13.5 8.5 8.5 13.5 2 7" />
                <polyline points="16 17 22 17 22 11" />
              </svg>
            )}
            {!trendUp && !trendDown && (
              <svg className="size-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
            )}
            {Math.abs(trend).toFixed(trend % 1 === 0 ? 0 : 1)}%
          </span>
          {trendLabel && (
            <span className="text-muted-foreground">{trendLabel}</span>
          )}
        </div>
      )}
    </article>
  );
}
